import toUpper from 'lodash/toUpper'
import { Configuration, Context } from '@nuxt/types'
import { env, coreUtils } from '@ustra/core'
import * as propertyStore from '@ustra/core/src/config/property-store'
import { getNuxtConfig } from '../utils/nuxt-utils'
import NuxtAppProperties from './nuxt-app-properties'

const PROPERTY_STORE_KEY = 'APP_PROPERTIES'
const PROFILE_STORE_KEY = 'CONFIG_ENV'

// 보안 항목 제외된 property 조회
const getClientProperties = <T extends NuxtAppProperties>(properties: T): T => {
  if (!properties.nuxt.secureClientProps) {
    return
  }

  // @ts-ignore
  const prop = coreUtils.deepMerge({}, properties)

  for (const secureClientProp of properties.nuxt.secureClientProps) {
    coreUtils.deleteProperty(prop, secureClientProp)
  }

  return prop
}

/**
 * property를 조회한다.
 * @param configStr
 */
export const getProperties = <T extends NuxtAppProperties>(): T => {
  const nuxtConfig = getNuxtConfig()
  let prop = null

  if (nuxtConfig && nuxtConfig[PROPERTY_STORE_KEY]) {
    prop = nuxtConfig[PROPERTY_STORE_KEY] as T
  }

  if (!prop) {
    prop = propertyStore.getProperties() as T
  }

  if (prop) {
    setProperties(prop)
  }

  return prop as T
}

/**
 * 프로퍼티를 세팅한다.
 * @param properties 프로퍼티
 * @param nuxtConfig nuxt configuration
 */
export const setProperties = <T extends NuxtAppProperties>(properties: T, nuxtConfig?: Configuration) => {
  propertyStore.setProperties(properties)
  propertyStore.setConfigEnv(properties.app.profile)

  if (!nuxtConfig) {
    return
  }

  if (!nuxtConfig.env) {
    nuxtConfig.env = {}
  }
  nuxtConfig.env[PROPERTY_STORE_KEY] = JSON.stringify(properties)
  nuxtConfig.env[PROFILE_STORE_KEY] = properties.app.profile

  if (!nuxtConfig.publicRuntimeConfig) {
    nuxtConfig.publicRuntimeConfig = {}
  }
  if (!nuxtConfig.privateRuntimeConfig) {
    nuxtConfig.privateRuntimeConfig = {}
  }

  nuxtConfig.privateRuntimeConfig[PROPERTY_STORE_KEY] = JSON.stringify(properties)
  nuxtConfig.publicRuntimeConfig[PROPERTY_STORE_KEY] = JSON.stringify(getClientProperties(properties))

  nuxtConfig.publicRuntimeConfig[PROFILE_STORE_KEY] = properties.app.profile
}

/**
 * config env 조회
 */
export const getConfigEnv = () => {
  const nuxtConfig = getNuxtConfig()
  let configEnv: string = null

  if (nuxtConfig && nuxtConfig[PROFILE_STORE_KEY]) {
    configEnv = nuxtConfig[PROFILE_STORE_KEY]
  }

  if (!configEnv) {
    configEnv = propertyStore.getConfigEnv()
  }
  return configEnv
}

/**
 * 현재 프로파일 정보를 조회한다.
 */
export const getCurrentProfile = () => {
  const configEnv = getConfigEnv()
  return env.utils.currentProfile(configEnv, env.Profile.PRODUCTION)
}

/**
 * client 상에서의 profile 정보를 지운다.
 */
export const removeClientProfileInfo = (context: Context) => {
  // @ts-ignore
  const config = context.$config

  if (config) {
    delete config[PROFILE_STORE_KEY]

    if (config[PROPERTY_STORE_KEY]) {
      coreUtils.deleteProperty(config[PROPERTY_STORE_KEY], 'app.profile')
    }
  }

  const env = context.env

  if (env) {
    delete env[PROFILE_STORE_KEY]
    delete env[PROPERTY_STORE_KEY]
  }
}

/**
 * profile별 설정 키 값을 조회한다.
 * @param profile
 */
export const getProfilePropertyKey = (profile: env.Profile) => {
  return PROPERTY_STORE_KEY + '_' + toUpper(env.utils.propertyProfileName(profile))
}

/**
 * 프로파일별 프로퍼티를 세팅한다.
 * @param properties 프로퍼티
 * @param nuxtConfig nuxt configuration
 * @param profile profile
 */
export const setProfileProperties = <T extends NuxtAppProperties>(properties: T, nuxtConfig: Configuration, profile: env.Profile) => {
  if (!nuxtConfig.publicRuntimeConfig) {
    nuxtConfig.publicRuntimeConfig = {}
  }
  if (!nuxtConfig.privateRuntimeConfig) {
    nuxtConfig.privateRuntimeConfig = {}
  }

  nuxtConfig.privateRuntimeConfig[getProfilePropertyKey(profile)] = JSON.stringify(properties)
  nuxtConfig.publicRuntimeConfig[getProfilePropertyKey(profile)] = JSON.stringify(getClientProperties(properties))
}

/**
 * 프로파일별 프로퍼티를 조회한다.
 * @param profile profile
 */
export const getProfileProperties = <T extends NuxtAppProperties>(profile: env.Profile): T => {
  const nuxtConfig = getNuxtConfig()
  let prop = null

  if (nuxtConfig && nuxtConfig[getProfilePropertyKey(profile)]) {
    prop = nuxtConfig[getProfilePropertyKey(profile)] as T
  }

  if (!prop) {
    prop = getProperties() as T
  }

  if (prop) {
    setProperties(prop)
  }

  return prop as T
}
